import classNames from "classnames";
import { useCallback, useEffect, useState } from "react";
import { PWSSessionState } from "@inferno/renderer-shared-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import Heading from "../../../../core/ui/Heading.component";
import { CoastMediaEpisodeNode, CoastMediaTabConfig } from "./CoastMedia.util";
import { CoastMediaEpisode } from "./CoastMediaEpisode.component";
import { CoastMediaTabPagination } from "./CoastMediaTabPagination.component";
import { EpisodesSkeletons } from "./CoastMediaTabsSkeleton.component";
import { coastMediaService } from "../../../../core/services/Pws";

const log = ILog.logger("CoastMediaTab.component");

interface CoastMediaTabProps {
  setCurrentEpisode: (episode: CoastMediaEpisodeNode, groupSlug: string) => void;
  sessionState: PWSSessionState;
  tab: CoastMediaTabConfig;
  isActiveTab: boolean;
}

export const CoastMediaTab = ({ isActiveTab, tab, setCurrentEpisode, sessionState }: CoastMediaTabProps) => {
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);
  const [episodes, setEpisodes] = useState(tab.episodesLL);
  const [resume, setResume] = useState(tab.resume);
  const [curPage, setCurPage] = useState(1);

  const getOnAirVideo = useCallback(async () => {
    if (!tab.hasLive) {
      return;
    }

    try {
      const { video } = await coastMediaService.getOnAirEpisode(sessionState.accessToken);

      if (video) {
        setEpisodes(episodes.prependData(video));
      } else {
        log.debug("No live video available");
      }
    } catch (e) {
      log.debug("Error fetching live episode", e);
    }
  }, [episodes, sessionState.accessToken, tab.hasLive]);

  const getNextPage = useCallback(async () => {
    // there might have been an issue loading episodes for a specific tab on backend
    // in that case config would still be returned, but episodes would be missing
    if (episodes.size && !resume) {
      return log.warn(`Can't get next page for tab "${tab.title}" - no resume`);
    }

    try {
      setIsLoadingNextPage(true);

      const { videos, resume: nextResume } =
        episodes.size && resume
          ? await coastMediaService.listMediaEpisodes(resume)
          : await coastMediaService.listMediaEpisodes(null, { groupSlug: tab.slug });

      setResume(nextResume);
      setEpisodes(episodes.appendDataMany(videos));
    } catch (e) {
      log.error("Error fetching next page", e);
    } finally {
      setIsLoadingNextPage(false);
    }
  }, [episodes, resume, tab.slug, tab.title]);

  const goToNextPage = useCallback(() => {
    setCurPage(curPage + 1);

    if (tab.episodesLL.pageCount < curPage + 1) {
      getNextPage();
    }
  }, [curPage, getNextPage, tab.episodesLL.pageCount]);

  useEffect(() => {
    getOnAirVideo();
    if (tab.slug !== "search") {
      getNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pageEpisodes = tab.episodesLL.getEpisodesForPage(curPage);

  return (
    <div className={classNames("component-coast-media-tab", { active: isActiveTab })}>
      {tab.enablePagination ? (
        <CoastMediaTabPagination
          totalPages={tab.pagination?.pages || tab.episodesLL.pageCount}
          goToPrevPage={() => setCurPage(curPage - 1)}
          isLoadingNext={isLoadingNextPage}
          goToNextPage={goToNextPage}
          currentPage={curPage}
        />
      ) : null}
      {tab.slug === "search" ? <Heading level={4}>{tab.title}</Heading> : null}
      <div className="episodes-container">
        {pageEpisodes.length ? (
          pageEpisodes.map(episode => (
            <CoastMediaEpisode
              setCurrentEpisode={setCurrentEpisode}
              key={episode.data.slug}
              groupSlug={tab.slug}
              episode={episode}
            />
          ))
        ) : (
          <EpisodesSkeletons count={12} />
        )}
      </div>
    </div>
  );
};
